import { useState, useEffect, useCallback } from 'react';
import { ExecutionScoreService } from '../services/executionScoreService';
import { ExecutionScoresAggregateResponse } from '../types/executionScore';

export const useExecutionScores = (executionId: string | undefined) => {
  const [scores, setScores] = useState<ExecutionScoresAggregateResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scoreService = ExecutionScoreService.getInstance();

  const loadScores = useCallback(async () => {
    if (!executionId) return;

    setIsLoading(true);
    setError(null);
    try {
      const data = await scoreService.getScoresForExecution(executionId);
      setScores(data);
    } catch (err) {
      console.error('Failed to load scores:', err);
      setError('Failed to load scores');
    } finally {
      setIsLoading(false);
    }
  }, [executionId, scoreService]);

  const submitScore = useCallback(async (score: number, note?: string) => {
    if (!executionId) return;

    setIsLoading(true);
    setError(null);
    try {
      await scoreService.submitScore({
        executionId,
        score,
        note,
      });
      await loadScores(); // Reload scores after submission
    } catch (err) {
      console.error('Failed to submit score:', err);
      setError('Failed to submit score');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [executionId, scoreService, loadScores]);

  useEffect(() => {
    loadScores();
  }, [loadScores]);

  return {
    scores,
    isLoading,
    error,
    submitScore,
    reloadScores: loadScores,
  };
};
